import { lazy, Suspense, useContext, useState } from 'react';
import { Box } from '@mui/material';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
    BIDDING_MANAGER_READ_ROLES,
    BIDDING_READ_ROLES,
    BOOKING_DASHBOARD_ROLES,
    BUDGET_READ_ROLES,
    FORECASTS_READ_ROLES,
    LOUVRE_READ_ROLES,
    Roles,
} from '@callmart/types';
import { budgetEnabled } from '../../config';
import UserContext from '../../store/User/UserContext';
import Profile from '../../pages/Profile';
import CalldeltaOverview from '../../pages/CalldeltaOverview';
import OSPBookings from '../../pages/OSPBookings';
import DailyOSPBookings from '../../pages/DailyOSPBookings';
import LocationDeltas from '../../pages/LocationsDeltas';
import Dashboard from '../../pages/Dashboard';
import HomePage from '../../pages/HomePage/HomePage';
import Impressum from '../Impressum';
import DataPrivacy from '../DataPrivacy';
import ProtectedRoute from '../Auth/ProtectedRoute';
import Events from '../../pages/Events';
import CapacityReport from '../../pages/CapacityReport';
import Potentials from '../../pages/Potentials';
import PotentialsTabs from '../../pages/PotentialsTabs';
import { PotentialsType } from '../../enums/PotentialsType';
import WorktypeLocations from '../../pages/WorktypeLocations';
import PerformedCapacityReport from '../../pages/PerformedCapacityReport';
import CapacityAnalysisTabs from '../../pages/CapacityAnalysisTabs';
import StrategicVolume from '../../pages/StrategicVolume';
import Worktypes from '../../pages/Worktypes';
import Forecasters from '../../pages/Forecasters';
import WFM from '../../pages/WFM';
import Tasks from '../../pages/Tasks';
import CapacityTypes from '../../pages/CapacityTypes';
import Channels from '../../pages/Channels';
import Capacities from '../../pages/Capacities';
import WorkingUnits from '../../pages/WorkingUnits';
import OfferedReport from '../../pages/OfferedReport';
import WorkingHours from '../../pages/WorkingHours';
import VolumeOverview from '../../pages/VolumeOverview';
import Reduction from '../../pages/Reduction';
import BiddingCalculated from '../../pages/BiddingCalculated';
import OSPBiddingCalculatedTotals from '../../pages/OSPBiddingCalculatedTotals';
import StrategicVolumeSettings from '../../pages/StrategicVolumeSettings';
import ApiKeys from '../../pages/ApiKeys';
import Loader from '../Loaders/Loader';
import ErrorPage from '../../pages/ErrorPage';
import NotFound from '../../pages/NotFound';
import Holidays from '../../pages/Holidays';
import Brands from '../../pages/Brands';
import BudgetLayout from '../../pages/Budget/BudgetLayout';
import BudgetOverview from '../../pages/Budget/BudgetOverview';
import BudgetScenarios from '../../pages/Budget/BudgetScenarios';
import BudgetScenariosConsolidated from '../../pages/Budget/BudgetScenariosConsolidated';
import BudgetScenarioDetail from '../../pages/Budget/BudgetScenarioDetail';
import NewBudgetScenario from '../../pages/Budget/NewBudgetScenario';
import BudgetBillingConfig from '../../pages/Budget/BudgetBillingConfig';
import BudgetWorkingUnitMapping from '../../pages/Budget/BudgetWorkingUnitMapping';
import BudgetPricesAbt from '../../pages/Budget/BudgetPricesAbt';
import { TitleContextProvider } from '../../store/Page/TitleContext';
import AppBars from './AppBars';
import DocumentTitle from './DocumentTitle';
import MainContent from './MainContent';

const OperativeOverview = lazy(() =>
    import('../../pages/Forecasts').then((module) => ({ default: module.OperativeOverview }))
);
const StrategicOverview = lazy(() =>
    import('../../pages/Forecasts').then((module) => ({ default: module.StrategicOverview }))
);
const DataManagement = lazy(() =>
    import('../../pages/Forecasts').then((module) => ({ default: module.DataManagement }))
);
const Louvre = lazy(() => import('../../pages/Forecasts').then((module) => ({ default: module.Louvre })));
// `/forecasts/louvre/*` also matches `/forecasts/louvre` itself and the old `/forecasts/louvre/settings`.
const LEGACY_LOUVRE_PATHS = ['/forecasts/louvre/*', '/forecasts/louvre2', '/forecasts/louvre3', '/forecasts/louvre4'];
const EventsAdjustments = lazy(() =>
    import('../../pages/Forecasts').then((module) => ({ default: module.EventsAdjustments }))
);
const NewForecast = lazy(() =>
    import('../../pages/Forecasts').then((forecasts) => ({ default: forecasts.NewForecast }))
);
const EditForecast = lazy(() =>
    import('../../pages/Forecasts').then((forecasts) => ({ default: forecasts.EditForecast }))
);
const ListForecasts = lazy(() =>
    import('../../pages/Forecasts').then((forecasts) => ({ default: forecasts.ListForecasts }))
);
const ForecastInfoPage = lazy(() =>
    import('../../pages/Forecasts').then((forecasts) => ({ default: forecasts.Forecast }))
);

function NavBar() {
    const loggedInUser = useContext(UserContext);
    const [title, setTitle] = useState<string | undefined>(undefined);

    return (
        <TitleContextProvider onChange={setTitle} title={title}>
            <DocumentTitle />
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <AppBars position="fixed" user={loggedInUser} />

                <MainContent user={loggedInUser}>
                    <Suspense fallback={<Loader />}>
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/dashboard" element={<ProtectedRoute component={Dashboard} />} />
                            <Route path="/profile" element={<ProtectedRoute component={Profile} />} />
                            <Route
                                path="/calldeltaOverview"
                                element={
                                    <ProtectedRoute
                                        component={CalldeltaOverview}
                                        allowedRoles={BOOKING_DASHBOARD_ROLES}
                                    />
                                }
                            />
                            <Route
                                path="/ospBookings"
                                element={<ProtectedRoute component={OSPBookings} allowedRole={Roles.CallmartOSP} />}
                            />
                            <Route
                                path="/dailyOSPBookings"
                                element={
                                    <ProtectedRoute
                                        component={DailyOSPBookings}
                                        allowedRoles={BOOKING_DASHBOARD_ROLES}
                                    />
                                }
                            />
                            <Route path="/locationDeltas" element={<ProtectedRoute component={LocationDeltas} />} />
                            <Route path="/impressum" element={<ProtectedRoute component={Impressum} />} />
                            <Route path="/dataprivacy" element={<ProtectedRoute component={DataPrivacy} />} />
                            <Route path="/events" element={<ProtectedRoute component={Events} />} />
                            <Route path="/capacityReport" element={<ProtectedRoute component={CapacityReport} />} />
                            <Route
                                path="/potentials"
                                element={
                                    <ProtectedRoute
                                        type={loggedInUser?.isOSPUser ? PotentialsType.CALL_CENTER : null}
                                        component={loggedInUser?.isTEFUser ? PotentialsTabs : Potentials}
                                    />
                                }
                            />

                            <Route
                                path="/capacityAnalysis"
                                element={
                                    <ProtectedRoute component={CapacityAnalysisTabs} allowedRole={Roles.CallmartWFM} />
                                }
                            />
                            <Route
                                path="/performedCapacityReport"
                                element={<ProtectedRoute component={PerformedCapacityReport} />}
                            />
                            <Route
                                path="/volume"
                                element={
                                    <ProtectedRoute
                                        component={VolumeOverview}
                                        allowedRoles={BIDDING_MANAGER_READ_ROLES}
                                    />
                                }
                            />
                            <Route
                                path="/volume/strategic"
                                element={
                                    <ProtectedRoute component={StrategicVolume} allowedRoles={BIDDING_READ_ROLES} />
                                }
                            />
                            <Route
                                path="/volume/offered"
                                element={
                                    <ProtectedRoute
                                        component={OfferedReport}
                                        allowedRoles={BIDDING_MANAGER_READ_ROLES}
                                    />
                                }
                            />
                            <Route
                                path="/reduction"
                                element={<ProtectedRoute component={Reduction} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/WFM"
                                element={<ProtectedRoute component={WFM} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/locations"
                                element={
                                    <ProtectedRoute component={WorktypeLocations} allowedRole={Roles.CallmartWFM} />
                                }
                            />
                            <Route
                                path="/forecasters"
                                element={<ProtectedRoute component={Forecasters} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/worktypes"
                                element={<ProtectedRoute component={Worktypes} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/tasks"
                                element={<ProtectedRoute component={Tasks} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/capacityTypes"
                                element={<ProtectedRoute component={CapacityTypes} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/channels"
                                element={<ProtectedRoute component={Channels} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/workingUnits"
                                element={<ProtectedRoute component={WorkingUnits} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/capacities"
                                element={<ProtectedRoute component={Capacities} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/workingHours"
                                element={<ProtectedRoute component={WorkingHours} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/holidays"
                                element={<ProtectedRoute component={Holidays} allowedRole={Roles.CallmartWFM} />}
                            />
                            <Route
                                path="/brands"
                                element={<ProtectedRoute component={Brands} allowedRole={Roles.CallmartWFM} />}
                            />
                            {budgetEnabled && (
                                <Route
                                    path="/budget"
                                    element={
                                        <ProtectedRoute component={BudgetLayout} allowedRoles={BUDGET_READ_ROLES} />
                                    }
                                >
                                    <Route index element={<BudgetOverview />} />
                                    <Route path="scenarios" element={<BudgetScenarios />} />
                                    {/* Static segments before scenarios/:id so they aren't captured as an id. */}
                                    <Route path="scenarios/new" element={<NewBudgetScenario />} />
                                    <Route path="scenarios/consolidated" element={<BudgetScenariosConsolidated />} />
                                    <Route path="scenarios/:id" element={<BudgetScenarioDetail />} />
                                    <Route path="billing-config" element={<BudgetBillingConfig />} />
                                    <Route path="prices-abt" element={<BudgetPricesAbt />} />
                                    <Route path="working-unit-mapping" element={<BudgetWorkingUnitMapping />} />
                                </Route>
                            )}
                            <Route
                                path="/strategic-volumes"
                                element={
                                    <ProtectedRoute
                                        component={StrategicVolumeSettings}
                                        allowedRole={Roles.CallmartWFM}
                                    />
                                }
                            />
                            <Route
                                path="/bidding-calculated"
                                element={
                                    <ProtectedRoute
                                        component={BiddingCalculated}
                                        allowedRoles={BIDDING_MANAGER_READ_ROLES}
                                    />
                                }
                            />
                            <Route
                                path="/bidding-calculated/totals"
                                element={
                                    <ProtectedRoute
                                        component={OSPBiddingCalculatedTotals}
                                        allowedRole={Roles.CallmartOSP}
                                    />
                                }
                            />
                            <Route
                                path="/apiKeys"
                                element={
                                    <ProtectedRoute
                                        component={ApiKeys}
                                        allowedRole={Roles.Admin}
                                        allowedPermissions={['manage:auth0']}
                                    />
                                }
                            />
                            <Route
                                path="/forecasts/operative"
                                element={
                                    <ProtectedRoute
                                        component={OperativeOverview}
                                        allowedRole={Roles.CallmartForecast}
                                    />
                                }
                            />
                            <Route
                                path="/forecasts/strategic"
                                element={
                                    <ProtectedRoute component={StrategicOverview} allowedRoles={FORECASTS_READ_ROLES} />
                                }
                            />
                            {/* CALL-809: Louvre lives in the Bidding menu at /louvre. The route exists even while the
                                module is off, for the preview users (LOUVRE_PREVIEW_USERS): the API decides, and the
                                page shows Not found for everyone else. Its former /forecasts URLs (the page, the demo
                                views and the old settings page) stay explicit redirects, so they never match
                                /forecasts/:id. */}
                            <Route
                                path="/louvre"
                                element={<ProtectedRoute component={Louvre} allowedRoles={LOUVRE_READ_ROLES} />}
                            />
                            {LEGACY_LOUVRE_PATHS.map((path) => (
                                <Route key={path} path={path} element={<Navigate to="/louvre" replace />} />
                            ))}
                            <Route
                                path="/forecasts/management"
                                element={
                                    <ProtectedRoute component={DataManagement} allowedRole={Roles.CallmartForecast} />
                                }
                            />
                            <Route
                                path="/forecasts/events-adjustments"
                                element={
                                    <ProtectedRoute
                                        component={EventsAdjustments}
                                        allowedRole={Roles.CallmartForecast}
                                    />
                                }
                            />
                            <Route
                                path="/forecasts/new"
                                element={
                                    <ProtectedRoute component={NewForecast} allowedRole={Roles.CallmartForecast} />
                                }
                            />
                            <Route
                                path="/forecasts/:id/edit"
                                element={
                                    <ProtectedRoute component={EditForecast} allowedRole={Roles.CallmartForecast} />
                                }
                            />
                            <Route
                                path="/forecasts/:id"
                                element={
                                    <ProtectedRoute component={ForecastInfoPage} allowedRoles={FORECASTS_READ_ROLES} />
                                }
                            />
                            <Route
                                path="/forecasts"
                                element={
                                    <ProtectedRoute component={ListForecasts} allowedRoles={FORECASTS_READ_ROLES} />
                                }
                            />
                            <Route path="/error" element={<ErrorPage />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Suspense>
                </MainContent>
            </Box>
        </TitleContextProvider>
    );
}

export default NavBar;
